// App shell for the tour-builder. Three views, switched by local state (no router):
//   - list          : browse every derived tour (TourListView)
//   - detail        : open one tour by slug (TourDetailView)
//   - quote-builder : build a quote slide and prepend it to a tour (QuoteSlideBuilder)
// The header shows the app title, a "New quote slide" entry, and a refresh. The Back
// button lives in each sub-view (wired to return to list/detail). CONTRACTS §10 patterns.

import { useState } from "react";
import { CONFIG_OK, missingConfig } from "./config";
import { useTours } from "./useTours";
import { TourListView } from "./views/TourListView";
import { TourDetailView } from "./views/TourDetailView";
import { QuoteSlideBuilder } from "./views/QuoteSlideBuilder";

/** Which view is showing: the tour list, one tour's detail, or the quote-slide builder. */
type View =
  | { kind: "list" }
  | { kind: "detail"; slug: string }
  | { kind: "quote-builder"; slug?: string };

export function App() {
  const [view, setView] = useState<View>({ kind: "list" });
  const store = useTours();

  return (
    <div className="app">
      <header className="app__header">
        <div className="app__title">
          <span className="app__title-main">Tour Builder</span>
          <span className="app__title-sub">browse · open · arrange</span>
        </div>
        <div
          style={{ marginLeft: "auto", display: "flex", gap: 12, alignItems: "center" }}
        >
          {view.kind !== "quote-builder" && (
            <button
              type="button"
              className="btn btn--small"
              onClick={() => setView({ kind: "quote-builder" })}
              title="Build a quote slide and add it to a tour"
            >
              ✎ New quote slide
            </button>
          )}
          <button
            type="button"
            className="btn btn--ghost app__refresh"
            onClick={() => void store.refresh()}
            title="Reload from server"
            disabled={store.loading}
          >
            {store.loading ? "↻…" : "↻"}
          </button>
        </div>
      </header>

      {!CONFIG_OK && (
        <div className="banner banner--warn">
          Missing build-time config: <code>{missingConfig().join(", ")}</code>. Set them in{" "}
          <code>.env</code> (see <code>.env.example</code>) and rebuild.
        </div>
      )}
      {store.error && (
        <div className="banner banner--error">
          {store.error}{" "}
          <button type="button" className="btn btn--tiny" onClick={() => void store.refresh()}>
            Retry
          </button>
        </div>
      )}

      <main className="app__main">
        {view.kind === "list" ? (
          <TourListView
            groups={store.groups}
            allArtifacts={store.allArtifacts}
            loading={store.loading}
            onOpenTour={(slug) => setView({ kind: "detail", slug })}
            exportTour={store.exportTour}
            exportState={store.exportState}
          />
        ) : view.kind === "detail" ? (
          <TourDetailView
            slug={view.slug}
            groups={store.groups}
            allArtifacts={store.allArtifacts}
            loading={store.loading}
            onBack={() => setView({ kind: "list" })}
            onAddQuoteSlide={() => setView({ kind: "quote-builder", slug: view.slug })}
            reorder={store.reorder}
            addToTour={store.addToTour}
            removeFromTour={store.removeFromTour}
            removeQuoteSlide={store.removeQuoteSlide}
            exportTour={store.exportTour}
            exportState={store.exportState}
          />
        ) : (
          <QuoteSlideBuilder
            groups={store.groups}
            initialSlug={view.slug}
            addQuoteSlide={store.addQuoteSlide}
            onDone={(slug) =>
              setView(slug ? { kind: "detail", slug } : { kind: "list" })
            }
          />
        )}
      </main>
    </div>
  );
}
